/* eslint-disable no-console */
// 这个文件是结构化日志的实现，需要使用原始 console API

type LogLevel = "debug" | "info" | "warn" | "error";

const MAX_SERIALIZE_DEPTH = 5;

const logMethod: Record<LogLevel, (message?: unknown, ...optionalParams: unknown[]) => void> = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

// 环境变量控制最小日志级别（默认为 debug）
const LOG_LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const MIN_LOG_LEVEL: LogLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined) ?? "debug";

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_ORDER[level] >= LOG_LEVEL_ORDER[MIN_LOG_LEVEL];
}

function getObjectTag(value: object): string {
  return Object.prototype.toString.call(value).slice(8, -1);
}

function serializeValue(value: unknown, seen: WeakSet<object>, depth: number): unknown {
  if (value instanceof Error) {
    return serializeError(value, seen, depth);
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "bigint") {
    return value.toString();
  }

  if (typeof value === "function") {
    return `[Function:${value.name || "anonymous"}]`;
  }

  if (typeof value === "symbol") {
    return value.toString();
  }

  if (typeof value !== "object") {
    return value;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "Invalid Date" : value.toISOString();
  }

  if (value instanceof URL) {
    return value.toString();
  }

  if (value instanceof URLSearchParams) {
    return value.toString();
  }

  if (typeof Headers !== "undefined" && value instanceof Headers) {
    return Object.fromEntries(value.entries());
  }

  if (value instanceof Map) {
    if (seen.has(value)) {
      return "[Circular]";
    }
    if (depth >= MAX_SERIALIZE_DEPTH) {
      return "[MaxDepth:Map]";
    }

    seen.add(value);
    return {
      type: "Map",
      size: value.size,
      entries: Array.from(value.entries()).map(([mapKey, mapValue]) => [
        serializeValue(mapKey, seen, depth + 1),
        serializeValue(mapValue, seen, depth + 1),
      ]),
    };
  }

  if (value instanceof Set) {
    if (seen.has(value)) {
      return "[Circular]";
    }
    if (depth >= MAX_SERIALIZE_DEPTH) {
      return "[MaxDepth:Set]";
    }

    seen.add(value);
    return {
      type: "Set",
      size: value.size,
      values: Array.from(value.values()).map((entry) => serializeValue(entry, seen, depth + 1)),
    };
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) {
      return "[Circular]";
    }
    if (depth >= MAX_SERIALIZE_DEPTH) {
      return "[MaxDepth:Array]";
    }

    seen.add(value);
    return value.map((entry) => serializeValue(entry, seen, depth + 1));
  }

  return serializeObject(value as Record<string, unknown>, seen, depth);
}

function serializeObject(
  value: Record<string, unknown>,
  seen: WeakSet<object>,
  depth: number,
): Record<string, unknown> | string {
  if (seen.has(value)) {
    return "[Circular]";
  }

  if (depth >= MAX_SERIALIZE_DEPTH) {
    return `[MaxDepth:${getObjectTag(value)}]`;
  }

  seen.add(value);

  const ctorName =
    typeof value.constructor?.name === "string" && value.constructor.name.length > 0
      ? value.constructor.name
      : "Object";
  const serializedEntries = Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) => [
      key,
      serializeValue(entryValue, seen, depth + 1),
    ]),
  );

  if (ctorName !== "Object" && ctorName !== "Array" && !("type" in serializedEntries)) {
    return {
      type: ctorName,
      ...serializedEntries,
    };
  }

  return serializedEntries;
}

// 序列化错误对象，提取关键信息
function serializeError(
  error: unknown,
  seen: WeakSet<object>,
  depth: number,
): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      stack: error.stack,
      // 保留错误对象的其他可枚举属性
      ...Object.fromEntries(
        Object.entries(error)
          .filter(([key]) => !["message", "name", "stack"].includes(key))
          .map(([key, value]) => [key, serializeValue(value, seen, depth + 1)]),
      ),
    };
  }
  return { value: serializeValue(error, seen, depth + 1) };
}

// 深度序列化 payload，处理 Error 对象和循环引用
function serializePayload(payload: Record<string, unknown>): Record<string, unknown> {
  const seen = new WeakSet<object>();
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    result[key] = serializeValue(value, seen, 0);
  }
  return result;
}

function serialize(event: string, payload: Record<string, unknown>): string {
  const serializedPayload = serializePayload(payload);
  const entry = {
    event,
    timestamp: new Date().toISOString(),
    ...serializedPayload,
  };
  return JSON.stringify(entry);
}

export function logStructured(
  level: LogLevel,
  event: string,
  payload: Record<string, unknown> = {},
): void {
  if (!shouldLog(level)) {
    return;
  }

  let message: string;
  try {
    message = serialize(event, payload);
  } catch (error) {
    message = JSON.stringify({
      event: "structured_log.serialize.failed",
      timestamp: new Date().toISOString(),
      original_event: event,
      serialize_error: serializeError(error, new WeakSet<object>(), 0),
    });
  }

  // 开发环境下同时输出易读格式
  if (import.meta.env.DEV) {
    logMethod[level](`[${level.toUpperCase()}] ${event}`, payload);
  } else {
    // 生产环境只输出 JSON
    logMethod[level](message);
  }
}

export function logDebug(event: string, payload?: Record<string, unknown>): void {
  logStructured("debug", event, payload ?? {});
}

export function logInfo(event: string, payload?: Record<string, unknown>): void {
  logStructured("info", event, payload ?? {});
}

export function logWarn(event: string, payload?: Record<string, unknown>): void {
  logStructured("warn", event, payload ?? {});
}

export function logError(event: string, payload?: Record<string, unknown>): void {
  logStructured("error", event, payload ?? {});
}

export const __structuredLogTestUtils = {
  serialize,
  serializePayload,
};
